// import 'expo/build/Expo.fx';

import { registerRootComponent } from 'expo';
import React, { createElement, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Platform } from 'react-native';

import api from './api/api';
import { installProductionConsoleGuard } from './common/logger';
import ActivityIndicatorScreen from './components/ActivityIndicatorScreen';
import environment from './environments';
import useCachedResources from './hooks/app/useCachedResources';

// [QHDEV-LOGGING] Silence console.* in release builds before any other module can use
// it, and route console.error into Crashlytics. No-op in development.
installProductionConsoleGuard();

api.setEnvironment(environment);

const injectWebCss = () => {
  const style = document.createElement('style');
  style.textContent = `textarea, select, input, button { outline: none !important; }`;
  return document.head.append(style);
};

export default function App() {
  const isLoadingComplete = useCachedResources();
  if (Platform.OS == 'web') injectWebCss();

  // Lazy load app to reduce app size for web-embedded components. WebEmbed is
  // used to load React Native components in ReactJS Web Apps such as the
  // admin portal.
  const isWebEmbed =
    Platform.OS === 'web' && window.location.pathname.includes('/web-embed');
  const isWebVirtualCarePortal =
    Platform.OS === 'web' &&
    window.location.pathname.includes('/virtual-care-portal');

  const AppComponent = lazy(() =>
    isWebEmbed
      ? import('./web-embed/WebEmbed')
      : isWebVirtualCarePortal
        ? import('./AppWebVirtualCarePortal')
        : import('./AppLazy')
  );
  if (!isLoadingComplete) {
    return null;
  } else {
    return (
      <Suspense fallback={<ActivityIndicatorScreen />}>
        <AppComponent />
      </Suspense>
    );
  }
}

// @see https://github.com/expo/expo/issues/18485
if (Platform.OS === 'web') {
  const rootTag = createRoot(
    document.getElementById('root') ?? document.getElementById('main')!
  );
  rootTag.render(createElement(App));
} else {
  registerRootComponent(App);
}
